import { blurAfterMouseClick } from './blurAfterMouseClick'

import styles from './KeywordAssist.module.scss'

import Button from '@/components/Button'
import { Spinner } from '@/components/Spinner'
import type {
  GtKeywordCounts,
  GtKeywordProgress,
  GtKeywordStatus,
} from '@/pages/app/AttributionViewer/annotate/useGtKeywords'

export interface KeywordAssistProps {
  status: GtKeywordStatus
  progress: GtKeywordProgress
  failedCount: number
  counts: GtKeywordCounts
  onIdentify: () => void
  onRetry: () => void
}

const categories = ['name', 'number', 'term', 'negation'] as const

const categorySummary = (counts: GtKeywordCounts): string =>
  categories
    .filter((category) => counts[category] > 0)
    .map((category) => `${counts[category]} ${category}${counts[category] === 1 ? '' : 's'}`)
    .join(' · ')

export const KeywordAssist = ({ status, progress, failedCount, counts, onIdentify, onRetry }: KeywordAssistProps) => (
  <section className={styles.assist} aria-label="Keyword verification" onClick={blurAfterMouseClick}>
    {status === 'idle' && (
      <div className={styles.idle}>
        <Button
          title="Identify keywords for verification"
          type="dark"
          width={460}
          className={styles.primary}
          onClick={onIdentify}
        />
        <p>
          Flags names, numbers, and key terms so you can check them against the audio. Tap ← / → to jump between them.
        </p>
      </div>
    )}
    {status === 'running' && (
      <div className={styles.running} role="status" aria-live="polite">
        <Spinner size={16} color="currentColor" />
        <span>
          Identifying keywords… {progress.done} of {progress.total} sections
        </span>
        <span className={styles.runningHint}>Keep annotating — highlights appear as each section finishes.</span>
      </div>
    )}
    {status === 'done' && (
      <div className={styles.done} role="status" aria-live="polite">
        <div className={styles.summary}>
          <span>
            {counts.total} keyword{counts.total === 1 ? '' : 's'} flagged
            {categorySummary(counts) ? ` · ${categorySummary(counts)}` : ''}
          </span>
          <span className={styles.legend}>
            <strong>verify against the audio</strong>
            <em>end of block — check it belongs to this speaker</em>
          </span>
          <span className={styles.jumpHint}>Tap ← / → to jump</span>
        </div>
        <Button title="Run again" type="light" width={104} onClick={onIdentify} />
      </div>
    )}
    {status === 'error' && (
      <div className={styles.error} role="status" aria-live="polite">
        <span>
          Couldn&apos;t finish: {failedCount} of {progress.total} sections failed.
        </span>
        <Button title="Retry" type="light" width={84} onClick={onRetry} />
      </div>
    )}
  </section>
)
